import { useEffect, useState } from 'react';
import { GeneratedPost, PreviewFormat, PREVIEW_FORMATS, CONTENT_CATEGORIES, isPastelUri, parsePastelUri } from '@agency-smm/shared';
import { NETWORK_ICONS } from '../../utils/networkIcons';
import {
  PostCardStyled, PostImage, HoverOverlay, PostCardFooter, SelectCheckbox,
  AiLoadingOverlay, AiLoadingVideo, AiLoadingScrim, AiLoadingText,
  PastelBackground, PostNetwork, CategoryBadge, ImageSourceBadge,
  CaptionPreview, getImageSourceLabel,
  RetryHold, RetryBadge, RetryText, RetryDetail, RetryBtn,
} from './styled';

// Tope de intentos, espejado del server (services/generation/retryQueue.ts).
const RETRY_MAX_ATTEMPTS = 5;

// El rótulo sale de la causa, NO es siempre "overloaded": esperar sólo arregla
// la sobrecarga, y prometerlo cuando la causa es otra manda a esperar al pedo.
const RETRY_LABEL: Record<string, string> = {
  overloaded: '⚡ AI OVERLOADED',
  unavailable: '⚡ AI UNAVAILABLE',
  blocked: '⚡ BLOCKED BY AI',
  other: '⚡ ON HOLD',
};
const RETRY_BLURB: Record<string, string> = {
  overloaded: 'The AI was busy. Waiting for it to free up.',
  unavailable: "The AI provider isn't accepting requests — usually out of credits.",
  blocked: 'The AI refused this prompt. Retrying may not help — try editing the topic.',
  other: "This post didn't generate. Waiting to try again.",
};

interface PostCardProps {
  post: GeneratedPost;
  effectiveFormat: PreviewFormat;
  compositeThumbnail?: string;
  // true cuando algún texto del post no entra en su caja y se recorta al render
  // (mismo layout que el export). Muestra un badge de aviso.
  illegible?: boolean;
  isSelected?: boolean;
  onSelectToggle?: (postId: string) => void;
  onOpen: (post: GeneratedPost) => void;
  // Acciones por-post (mismas que en QuickGen). Opcionales: si no se pasan, no
  // se muestran los botones.
  onLike?: (post: GeneratedPost) => void;
  onDislike?: (post: GeneratedPost) => void;
  onMakeVariations?: (post: GeneratedPost, count: number) => void;
  onRetry?: (post: GeneratedPost) => void;
  /** Reintenta AHORA un post que quedó en espera (no cuesta crédito). */
  onRetryPending?: (post: GeneratedPost) => void;
  liked?: boolean;
  actionBusy?: boolean;
}

const actionBtn: React.CSSProperties = {
  width: 30, height: 30, borderRadius: 999, border: 'none', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
  color: '#fff', background: 'rgba(15,23,42,0.72)', lineHeight: 1,
};

export function PostCard({
  post, effectiveFormat, compositeThumbnail, illegible,
  isSelected, onSelectToggle, onOpen,
  onLike, onDislike, onMakeVariations, onRetry, onRetryPending, liked, actionBusy,
}: PostCardProps) {
  const showCheckbox = onSelectToggle !== undefined;
  const [varOpen, setVarOpen] = useState(false);
  const hasImage = !!post.imageUrl;
  // El post no se generó y está esperando otro intento. Ocupa la tarjeta entera:
  // no hay imagen ni texto que mostrar, y disimularlo lo haría pasar por normal.
  const waiting = post.retryState === 'pending' || post.retryState === 'exhausted';
  const showActions = !!(onLike || onDislike || onMakeVariations || onRetry) && !post.pendingImageTaskId && !waiting;
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // La carta toma el aspect ratio REAL de la imagen mostrada (medido al cargar),
  // así el widescreen se ve ancho y el vertical alto — el masonry los acomoda.
  // Fallback al formato mientras carga / para pastel (bloque de color sólido).
  const displaySrc = post.imageUrl && !isPastelUri(post.imageUrl)
    ? (compositeThumbnail || post.imageUrl)
    : null;
  // Texto a mostrar en la carta: el headline del template si existe, si no el
  // caption. Da una vista rápida del contenido generado sin abrir el post.
  const previewText = (post.dynamicTexts?.headline || post.content || '').trim();
  const [naturalRatio, setNaturalRatio] = useState<number | null>(null);
  useEffect(() => { setNaturalRatio(null); }, [displaySrc]);
  const aspectRatio = naturalRatio ?? PREVIEW_FORMATS[effectiveFormat].aspectRatio;
  const onImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (w && h) setNaturalRatio(w / h);
  };

  return (
    <PostCardStyled onClick={() => onOpen(post)}>
      {showCheckbox && (
        <SelectCheckbox
          $selected={!!isSelected}
          onClick={(e) => {
            e.stopPropagation();
            onSelectToggle!(post.id);
          }}
        >
          {isSelected ? '✓' : ''}
        </SelectCheckbox>
      )}
      <PostImage $aspectRatio={aspectRatio}>
        {waiting ? (
          <RetryHold $exhausted={post.retryState === 'exhausted'} onClick={stop}>
            <RetryBadge>{RETRY_LABEL[post.retryKind || 'other']}</RetryBadge>
            <RetryText>
              {post.retryState === 'exhausted'
                ? `Gave up after ${post.retryAttempts ?? 0} attempts.`
                : RETRY_BLURB[post.retryKind || 'other']}
            </RetryText>
            {post.retryState === 'pending' && (post.retryAttempts ?? 0) > 0 && (
              <RetryText>Retrying automatically — attempt {post.retryAttempts} of {RETRY_MAX_ATTEMPTS}</RetryText>
            )}
            {post.retryDetail && post.retryKind === 'other' && (
              <RetryDetail title={post.retryDetail}>{post.retryDetail}</RetryDetail>
            )}
            {onRetryPending && (
              <RetryBtn type="button" disabled={actionBusy}
                onClick={(e) => { stop(e); onRetryPending(post); }}>
                {actionBusy ? 'Retrying…' : 'Retry now'}
              </RetryBtn>
            )}
          </RetryHold>
        ) : post.pendingImageTaskId && !post.imageUrl ? (
          <AiLoadingOverlay>
            <AiLoadingVideo src="/loaders/ai-processing-square.mp4" autoPlay muted loop playsInline />
            <AiLoadingScrim />
            <AiLoadingText>Generating AI image…</AiLoadingText>
          </AiLoadingOverlay>
        ) : post.imageUrl ? (
          compositeThumbnail ? (
            <img src={compositeThumbnail} alt="Post" onLoad={onImgLoad} />
          ) : isPastelUri(post.imageUrl) ? (
            <PastelBackground $color={parsePastelUri(post.imageUrl) || '#F0F0F0'} />
          ) : (
            <img src={post.imageUrl} alt="Post" onLoad={onImgLoad} />
          )
        ) : null}

        {showActions && (
          <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6, zIndex: 4 }} onClick={stop}>
            {onRetry && (
              <button type="button" title="Regenerate image" disabled={actionBusy}
                style={{ ...actionBtn, opacity: actionBusy ? 0.5 : 1 }}
                onClick={(e) => { stop(e); onRetry(post); }}>↻</button>
            )}
            {onMakeVariations && (
              <button type="button" title="Make variations" disabled={!hasImage || actionBusy}
                style={{ ...actionBtn, opacity: !hasImage || actionBusy ? 0.5 : 1 }}
                onClick={(e) => { stop(e); setVarOpen((v) => !v); }}>✨</button>
            )}
            {onLike && (
              <button type="button" title={liked ? 'Liked — saved to gallery' : 'Like (save to gallery)'} disabled={!hasImage || actionBusy}
                style={{ ...actionBtn, background: liked ? '#DC2626' : actionBtn.background, opacity: !hasImage || actionBusy ? 0.5 : 1 }}
                onClick={(e) => { stop(e); onLike(post); }}>{liked ? '♥' : '♡'}</button>
            )}
            {onDislike && (
              <button type="button" title="Dislike (delete this ad)" disabled={actionBusy}
                style={{ ...actionBtn, background: 'rgba(220,38,38,0.85)', opacity: actionBusy ? 0.5 : 1 }}
                onClick={(e) => { stop(e); onDislike(post); }}>🗑</button>
            )}
          </div>
        )}
        {varOpen && onMakeVariations && (
          <div style={{ position: 'absolute', top: 44, right: 8, display: 'flex', gap: 4, background: 'rgba(15,23,42,0.92)', padding: 5, borderRadius: 8, zIndex: 5 }} onClick={stop}>
            {[1, 2, 3].map((n) => (
              <button key={n} type="button"
                style={{ width: 26, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer', background: '#fff', fontWeight: 700 }}
                onClick={(e) => { stop(e); setVarOpen(false); onMakeVariations(post, n); }}>{n}</button>
            ))}
          </div>
        )}
      </PostImage>
      <HoverOverlay>
        <div style={{ fontSize: 13, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {post.content}
        </div>
      </HoverOverlay>
      {previewText && <CaptionPreview>{previewText}</CaptionPreview>}
      <PostCardFooter>
        <PostNetwork>{NETWORK_ICONS[post.network] || '📱'} {post.network}</PostNetwork>
        {post.contentCategory && (
          <CategoryBadge $category={post.contentCategory}>
            {CONTENT_CATEGORIES[post.contentCategory]?.label || post.contentCategory}
          </CategoryBadge>
        )}
        {post.imageSource && (
          <ImageSourceBadge $source={post.imageSource}>
            {getImageSourceLabel(post.imageSource, post.aiModel, post.aiModelDetail)}
          </ImageSourceBadge>
        )}
        {post.topic && (
          // De qué tema salió este post. Con multi-tema una tanda mezcla 5-10
          // temas, y sin esto no hay forma de saber cuál produjo cuál.
          <span
            title={post.topic}
            style={{ fontSize: 10, fontWeight: 600, color: '#3730A3', background: '#E0E7FF', border: '1px solid #C7D2FE', borderRadius: 999, padding: '2px 7px', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            💡 {post.topic}
          </span>
        )}
        {post.aiFallback && (
          // Rojo y explícito a propósito: en ámbar y con texto genérico el usuario
          // asumía que la imagen de stock ERA la generación de IA. El motivo real
          // (sin saldo, prompt bloqueado, timeout…) va debajo de la tarjeta.
          <span
            title={post.aiFallbackReason || "The AI image couldn't be generated; a fallback image was used. You can regenerate this post."}
            style={{ fontSize: 10, fontWeight: 700, color: '#991B1B', background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 999, padding: '2px 7px', whiteSpace: 'nowrap' }}
          >
            ⚠️ Not AI — fallback
          </span>
        )}
        {illegible && (
          <span
            title="Some text doesn't fit its box and gets cut off at full export size. Open the editor to shorten it or enlarge the text box."
            style={{ fontSize: 10, fontWeight: 700, color: '#9A3412', background: '#FFEDD5', borderRadius: 999, padding: '2px 7px', whiteSpace: 'nowrap' }}
          >
            ✂️ Text cut off
          </span>
        )}
      </PostCardFooter>
      {post.aiFallback && post.aiFallbackReason && (
        // El motivo va visible, no solo en el tooltip: el punto de todo esto es
        // que se sepa QUÉ falló sin tener que abrir los logs del Worker.
        <div
          style={{ margin: '0 12px 10px', padding: '6px 8px', fontSize: 11, lineHeight: 1.35, color: '#7F1D1D', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6 }}
        >
          {post.aiFallbackReason}
        </div>
      )}
    </PostCardStyled>
  );
}
